import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { GqlCourse, GqlPermissions } from "~/__generated__";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaDataGrid from "~/modules/app/components/GaiaTable";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import CourseUserProtocolForm from "~/modules/settings/course/components/CourseUserProtocol";

import useProtocolCourseCreate from "../../routes/ProtocolEditRoute/api/useProtocolCourseCreate";
import useProtocolCourseDelete from "../../routes/ProtocolEditRoute/api/useProtocolCourseDelete";

type Props = {
    data: GqlCourse[];
};

export default function ProtocolCourseList({ data }: Props) {
    const { t } = useTranslation("protocol");
    const { protocolId } = useParams();
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    if (!protocolId) throw new Error("No Protocol ID in Route, Routing is not configured correctly");

    const { deleteProtocolCourse, loading: deleteLoading, error: deleteError } = useProtocolCourseDelete();

    const { createProtocolCourse, loading: createLoading, error: createError } = useProtocolCourseCreate();

    const handleProtocolCourseDelete = async (courseId: string) => {
        await deleteProtocolCourse({
            protocolId,
            courseId,
        });
    };

    if (!data && !deleteLoading && (deleteError || createError)) {
        return <ErrorScreen error={deleteError || createError} />;
    }

    return (
        <>
            {isAdmin && (
                <CourseUserProtocolForm
                    excludeAssignedToProtocolId={protocolId}
                    onSubmitted={(courseId: string): void => {
                        createProtocolCourse({
                            protocolId,
                            courseId,
                        });
                    }}
                />
            )}
            <GaiaDataGrid
                rows={data}
                columns={[
                    {
                        field: "name",
                        headerName: t("protocolCourseList.colName"),
                        flex: 1,
                    },
                    {
                        field: "description",
                        headerName: t("protocolCourseList.colDescription"),
                        flex: 1,
                    },
                    ...(isAdmin
                        ? [
                              {
                                  type: "actions" as const,
                                  field: "actions",
                                  headerName: t("protocolCourseList.colDelete"),
                                  flex: 1,
                                  renderCell: (params: { row: { id: string } }) => (
                                      <GaiaDeleteButton
                                          label=""
                                          isLoading={deleteLoading}
                                          onDelete={() => handleProtocolCourseDelete(params.row.id)}
                                      />
                                  ),
                              },
                          ]
                        : []),
                ]}
                loading={deleteLoading || createLoading}
                rowCount={data.length}
            />
        </>
    );
}
